import { useCallback, useEffect, useMemo, useState } from "react";
import {
  emailTemplatesList,
  emailTemplatesMarkSeen,
  emailTemplateGet,
  emailTemplateSave,
  emailTemplateReset,
  emailTemplateSendTest,
  emailTemplateVersions,
  emailTemplatePreview,
  emailTemplateRestore,
  emailTemplateVersionDelete,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PreviewEnvNotice } from "@/components/PreviewEnvNotice";
import RichTextEditor from "@/components/RichTextEditor";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Mail, Loader2, Save, RotateCcw, Eye, Copy, Send, ChevronRight, ChevronDown, History, Trash2,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORY_ORDER = [
  "Onboarding & Aktivierung",
  "Testphase & Conversion",
  "Reaktivierung & Win-back",
  "Zahlungen & Abrechnung",
  "Account & Sicherheit",
  "Mandantenfähigkeit",
  "Reporting & Insights",
  "Intern & Admin",
];

export default function EmailTemplates() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [detail, setDetail] = useState(null);
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [buttonLabel, setButtonLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [rev, setRev] = useState(0);
  const [openCats, setOpenCats] = useState({});
  const [versions, setVersions] = useState([]);
  const [showOlder, setShowOlder] = useState(false);
  const [delVersion, setDelVersion] = useState(null);
  // Test-Mail-Dialog
  const [testOpen, setTestOpen] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const l = await emailTemplatesList();
      setList(l);
      if (l.length && !selectedKey) setSelectedKey(l[0].key);
    } catch {
      toast.error("Vorlagen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    emailTemplatesMarkSeen().catch(() => {});
    loadList();
  }, [loadList]);

  // Nach Kategorie gruppieren (Reihenfolge per CATEGORY_ORDER).
  const grouped = useMemo(() => {
    const map = {};
    for (const t of list) (map[t.category] = map[t.category] || []).push(t);
    const cats = Object.keys(map).sort(
      (a, b) => CATEGORY_ORDER.indexOf(a) - CATEGORY_ORDER.indexOf(b)
    );
    return cats.map((c) => ({ category: c, items: map[c] }));
  }, [list]);

  // Beim Laden bleiben alle Kategorien eingeklappt; der Nutzer öffnet gezielt eine Gruppe.

  // Kategorie umschalten: andere einklappen und zur ersten Mail der Gruppe springen.
  const selectCategory = (cat, items) => {
    if (openCats[cat]) {
      setOpenCats({});
    } else {
      setOpenCats({ [cat]: true });
      if (items.length) setSelectedKey(items[0].key);
    }
  };

  const loadVersions = useCallback(async (key) => {
    if (!key) return;
    try {
      setVersions(await emailTemplateVersions(key));
    } catch {
      setVersions([]);
    }
  }, []);

  const loadDetail = useCallback(async (key) => {
    if (!key) return;
    try {
      const d = await emailTemplateGet(key);
      setDetail(d);
      setName(d.name);
      setSubject(d.subject);
      setBody(d.body);
      setButtonLabel(d.button_label || "");
      setRev((r) => r + 1);
      setShowOlder(false);
      loadVersions(key);
    } catch {
      toast.error("Vorlage konnte nicht geladen werden.");
    }
  }, [loadVersions]);

  useEffect(() => {
    setDetail(null);
    loadDetail(selectedKey);
  }, [selectedKey, loadDetail]);

  const applyDetail = (d) => {
    setDetail(d);
    setName(d.name);
    setSubject(d.subject);
    setBody(d.body);
    setButtonLabel(d.button_label || "");
    setRev((r) => r + 1);
    setList((prev) =>
      prev.map((t) =>
        t.key === selectedKey ? { ...t, name: d.name, customized: d.customized } : t
      )
    );
    loadVersions(selectedKey);
  };

  const fmtTs = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  const previewSelf = async () => {
    try {
      const res = await emailTemplatePreview(selectedKey, {
        subject: subject.trim(),
        body,
        button_label: buttonLabel,
      });
      setPreviewData(res.preview);
      setPreviewOpen(true);
    } catch {
      toast.error("Vorschau konnte nicht geladen werden.");
    }
  };

  const previewVersion = async (v) => {
    try {
      const res = await emailTemplatePreview(selectedKey, {
        subject: v.subject,
        body: v.body,
        button_label: v.button_label || "",
      });
      setPreviewData(res.preview);
      setPreviewOpen(true);
    } catch {
      toast.error("Vorschau konnte nicht geladen werden.");
    }
  };

  const restore = async (v) => {
    try {
      const d = await emailTemplateRestore(selectedKey, v.id);
      applyDetail(d);
      toast.success(
        v.is_default ? "Standardeinstellung wiederhergestellt." : "Version wiederhergestellt."
      );
    } catch {
      toast.error("Wiederherstellen fehlgeschlagen.");
    }
  };

  const deleteVersion = async () => {
    if (!delVersion) return;
    try {
      const updated = await emailTemplateVersionDelete(selectedKey, delVersion.id);
      setVersions(updated);
      toast.success("Version gelöscht.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Löschen fehlgeschlagen.");
    } finally {
      setDelVersion(null);
    }
  };

  const renderVersionRow = (v) => (
    <div key={v.id} className="px-3 py-2" data-testid={`version-${v.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-sm font-medium truncate">
            {v.is_default ? "Standardeinstellung" : v.name || "Änderung"}
          </span>
          {!v.is_default && (
            <span className="text-xs text-muted-foreground shrink-0">{fmtTs(v.created_at)}</span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => previewVersion(v)}
            className="h-8 w-8 p-0"
            title="Vorschau"
            data-testid={`version-preview-${v.id}`}
          >
            <Eye className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => restore(v)}
            className="h-8 w-8 p-0 text-muted-foreground"
            title="Wiederherstellen"
            data-testid={`version-restore-${v.id}`}
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </Button>
          {!v.is_default && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDelVersion(v)}
              className="h-8 w-8 p-0 text-muted-foreground hover:text-red-600"
              title="Version löschen"
              data-testid={`version-delete-${v.id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  const save = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Betreff und Textkörper dürfen nicht leer sein.");
      return;
    }
    setSaving(true);
    try {
      const d = await emailTemplateSave(selectedKey, {
        name: name.trim(),
        subject: subject.trim(),
        body,
        button_label: buttonLabel,
      });
      applyDetail(d);
      toast.success("Vorlage gespeichert.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      const d = await emailTemplateReset(selectedKey);
      applyDetail(d);
      toast.success("Auf Standard zurückgesetzt.");
    } catch {
      toast.error("Zurücksetzen fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const res = await emailTemplateSendTest(selectedKey, {
        subject: subject.trim(),
        body,
        button_label: buttonLabel,
        to: testTo.trim(),
      });
      if (res.status === "sent") {
        toast.success(`Test-Mail an ${res.to} gesendet.`);
        setTestOpen(false);
      } else if (res.status === "not_configured") {
        toast.error(res.detail || "E-Mail-Versand ist nicht konfiguriert.");
      } else {
        toast.error("Test-Mail konnte nicht gesendet werden.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Versand fehlgeschlagen.");
    } finally {
      setSending(false);
    }
  };

  const copyToken = async (token) => {
    try {
      await navigator.clipboard.writeText(token);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = token;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch {
        /* ignore */
      }
      ta.remove();
    }
    toast.success(`${token} kopiert – an gewünschter Stelle einfügen.`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-templates-page">
      <PreviewEnvNotice area="E-Mail-Vorlagen" />
      <div className="flex items-center gap-3">
        <Mail className="h-7 w-7 text-primary" />
        <h1 className="text-2xl font-semibold">
          E-Mail-Verwaltung
          {list.length > 0 && (
            <span className="ml-2 font-normal text-muted-foreground" data-testid="template-total-count">
              ({list.length})
            </span>
          )}
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        {/* Kategorisiertes Menü */}
        <div
          className="border border-border bg-white h-fit lg:sticky lg:top-[120px] lg:self-start lg:max-h-[calc(100vh-140px)] lg:overflow-y-auto"
          data-testid="template-list"
        >
          {grouped.map(({ category, items }) => {
            const open = openCats[category];
            const newInCat = items.filter((t) => t.is_new).length;
            return (
              <div key={category} className="border-b border-border last:border-b-0">
                <button
                  type="button"
                  onClick={() => selectCategory(category, items)}
                  data-testid={`category-${category}`}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-left bg-muted/40 hover:bg-muted transition-colors"
                >
                  {open ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  )}
                  <span className="font-semibold text-sm flex-1">{category}</span>
                  {newInCat > 0 && (
                    <span className="inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] h-4 min-w-4 px-1">
                      {newInCat}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">{items.length}</span>
                </button>
                <div
                  className={`grid transition-[grid-template-rows] duration-200 ease-out ${
                    open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="divide-y divide-border">
                      {items.map((t) => (
                        <button
                          key={t.key}
                          type="button"
                          onClick={() => setSelectedKey(t.key)}
                          data-testid={`template-item-${t.key}`}
                          className={`w-full text-left pl-9 pr-3 py-2.5 transition-colors flex items-center gap-2 ${
                            selectedKey === t.key ? "bg-primary/10" : "hover:bg-muted/50"
                          }`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-sm truncate flex items-center gap-2">
                              {t.name}
                              {t.is_new && (
                                <span className="inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] h-4 px-1.5">
                                  NEU
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Editor */}
        {detail && (
          <div className="space-y-4" data-testid="template-editor">
            <div className="space-y-2">
              <Label htmlFor="tpl-name">Bezeichnung</Label>
              <Input
                id="tpl-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-white"
                placeholder={detail.default_name}
                data-testid="template-name"
              />
              <div className="text-xs text-muted-foreground space-y-0.5 pt-1" data-testid="template-meta">
                {detail.description && <p>{detail.description}</p>}
                {detail.meta?.recipient && (
                  <p><span className="font-medium text-foreground">Empfänger:</span> {detail.meta.recipient}</p>
                )}
                {detail.meta?.trigger && (
                  <p><span className="font-medium text-foreground">Auslöser:</span> {detail.meta.trigger}</p>
                )}
                {detail.meta?.timing && (
                  <p><span className="font-medium text-foreground">Zeitpunkt:</span> {detail.meta.timing}</p>
                )}
              </div>
            </div>

            <hr className="border-t border-border" style={{ marginTop: "30px", marginBottom: "30px" }} />

            <div className="space-y-2">
              <Label htmlFor="tpl-subject">Betreff</Label>
              <Input
                id="tpl-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="bg-white"
                data-testid="template-subject"
              />
            </div>

            <div className="space-y-2">
              <Label>Textkörper</Label>
              <RichTextEditor
                key={`${selectedKey}-${rev}`}
                value={body}
                onChange={setBody}
                testId="template-body"
              />
            </div>

            <div className="border border-border bg-muted/30 p-3 rounded-md">
              <div className="text-xs font-medium text-muted-foreground mb-2">
                Verfügbare Platzhalter (klicken zum Kopieren):
              </div>
              <div className="flex flex-wrap gap-2">
                {detail.variables.map((v) => (
                  <button
                    key={v.token}
                    type="button"
                    onClick={() => copyToken(v.token)}
                    title={v.description}
                    data-testid={`token-${v.token.replace(/[{}]/g, "")}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-2.5 py-1 text-xs font-mono hover:bg-primary/10 hover:border-primary/30 transition-colors"
                  >
                    <Copy className="h-3 w-3" />
                    {v.token}
                  </button>
                ))}
              </div>
            </div>

            {detail.has_button && (
              <div className="space-y-2">
                <Label htmlFor="tpl-button-label">Button-Text</Label>
                <Input
                  id="tpl-button-label"
                  value={buttonLabel}
                  onChange={(e) => setButtonLabel(e.target.value)}
                  className="bg-white"
                  placeholder={detail.default_button_label}
                  data-testid="template-button-label"
                />
                <p className="text-xs text-muted-foreground">
                  Beschriftung der Schaltfläche (Platzhalter <code>{"{{button}}"}</code>). Das Ziel
                  des Buttons bleibt aus Sicherheitsgründen fest.
                </p>
              </div>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              <Button onClick={save} disabled={saving} className="gap-2" data-testid="template-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Speichern
              </Button>
              <Button
                variant="secondary"
                onClick={previewSelf}
                className="gap-2 border border-border"
                data-testid="template-preview"
              >
                <Eye className="h-4 w-4" /> Vorschau
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setTestTo(user?.email || "");
                  setTestOpen(true);
                }}
                className="gap-2 border border-border"
                data-testid="template-send-test"
              >
                <Send className="h-4 w-4" /> Test-Mail senden
              </Button>
              {detail.customized && (
                <Button
                  variant="ghost"
                  onClick={reset}
                  disabled={saving}
                  className="gap-2 text-muted-foreground ml-auto"
                  data-testid="template-reset"
                >
                  <RotateCcw className="h-4 w-4" /> Auf Standard zurücksetzen
                </Button>
              )}
            </div>

            {/* Änderungsprotokoll */}
            <div className="border border-border rounded-md" data-testid="version-log">
              <div className="px-3 py-2 border-b border-border text-xs font-medium text-muted-foreground flex items-center gap-2">
                <History className="h-3.5 w-3.5" /> Änderungsprotokoll
              </div>
              <div className="divide-y divide-border">
                {versions.slice(0, 3).map(renderVersionRow)}
              </div>
              {versions.length > 3 && (
                <div className="border-t border-border">
                  <button
                    type="button"
                    onClick={() => setShowOlder((o) => !o)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-muted/50 transition-colors"
                    data-testid="versions-older-toggle"
                  >
                    {showOlder ? (
                      <ChevronDown className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5" />
                    )}
                    Ältere Versionen ({versions.length - 3})
                  </button>
                  <div
                    className={`grid transition-[grid-template-rows] duration-200 ease-out ${
                      showOlder ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="divide-y divide-border border-t border-border">
                        {versions.slice(3).map(renderVersionRow)}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Vorschau */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl" data-testid="template-preview-dialog">
          <DialogHeader>
            <DialogTitle>Vorschau (zuletzt gespeicherte Version)</DialogTitle>
          </DialogHeader>
          {previewData && (
            <div className="space-y-2">
              <div className="text-sm">
                <span className="text-muted-foreground">Betreff:</span>{" "}
                <span className="font-medium">{previewData[0]}</span>
              </div>
              <iframe
                title="E-Mail-Vorschau"
                srcDoc={previewData[1]}
                className="w-full h-[60vh] border border-border rounded-md bg-white"
                data-testid="template-preview-iframe"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Test-Mail */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent data-testid="template-test-dialog">
          <DialogHeader>
            <DialogTitle>Test-Mail senden</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="test-to">Empfänger-Adresse</Label>
            <Input
              id="test-to"
              type="email"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="name@firma.de"
              className="bg-white"
              data-testid="template-test-to"
            />
            <p className="text-xs text-muted-foreground">
              Es wird der aktuelle (auch ungespeicherte) Stand mit Beispiel-Platzhaltern versendet.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTestOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={sendTest} disabled={sending} className="gap-2" data-testid="template-test-send">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Version löschen – Rückfrage */}
      <Dialog open={!!delVersion} onOpenChange={(o) => !o && setDelVersion(null)}>
        <DialogContent data-testid="version-delete-dialog">
          <DialogHeader>
            <DialogTitle>Version löschen?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Möchten Sie die Version{" "}
            <span className="font-medium text-foreground">{delVersion?.name || "Änderung"}</span>
            {delVersion?.created_at ? ` vom ${fmtTs(delVersion.created_at)}` : ""} wirklich
            unwiderruflich löschen? Die aktuell gespeicherte Vorlage bleibt davon unberührt.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDelVersion(null)}>
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={deleteVersion}
              className="gap-2"
              data-testid="version-delete-confirm"
            >
              <Trash2 className="h-4 w-4" /> Löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
